import {
  choicesAttribute,
  choicesFrom,
  exerciseAttribute,
  freeAttribute,
  handedOverAttribute,
  missedAttribute,
  queueAttribute,
  recordAttribute,
  repliesAttribute,
  triesAttribute,
  uncheckedAttribute,
  versionAttribute,
} from "../content/lianxi/exercise-fields.js";
import { itemGate } from "./lianxi-gate.js";
import { blockHandoverCount } from "./lianxi-handover.js";
import { loadPlace, wantsPlace } from "./lianxi-place.js";
import { ExercisePress } from "./lianxi-post.js";
import {
  optionsOf,
  type YueduKeeper,
  type YueduPlace,
  type YueduReviewParts,
  YueduExercise,
} from "./lianxi-yuedu.js";
import { type HearVerdict, verdictSounds } from "./lianxi-verdict-sounds.js";
import {
  modified,
  optionByKey,
  optionRow,
  ownEnter,
  pressedRow,
  typingInto,
} from "./lianxi-xuanze.js";
import { said } from "./xiezi-parts.js";

/**
 * Starting the reading Exercise under a passage on its `/yuedu/` page.
 *
 * `partials/lianxi/yuedu.html` draws the passage and every question, and
 * nothing is fetched but a signed-in reader's place. `./lianxi-place.ts` asks
 * the handler for it first and writes it onto the block, with
 * `data-cb-lianxi-record` saying where to post, and the Exercise starts at
 * the question the handler left it at. A block with no place on it marks in
 * the browser and keeps nothing. The block is marked ready once the Exercise
 * is built, which is what the browser case waits for.
 *
 * **The place is asked for once the questions come near** (CB-64). Asking
 * opens the reader's row, which `/user/lianxi/` then lists as an Exercise in
 * progress. The questions share a page with the text now, and a reader who
 * only reads the text has started nothing, so the page asks when the
 * questions are a screen away or nearer, or when anything in them takes the
 * focus. A start is still counted on the first answer, as before.
 *
 * The passage's words are started apart from the questions, by
 * `./yuedu-start.ts`: the popover on every word of the passage, and a
 * signed-in reader's 生词 marked over the passage, the questions and the
 * options. `./main-yuedu.ts` starts both.
 */

/** The block, carrying the passage and every question. */
const exerciseSelector = "[data-cb-lianxi-yuedu]";

/** A list of whole numbers off an attribute, as `3,0,5`. */
function indexes(value: string): readonly number[] {
  return value === ""
    ? []
    : value
        .split(",")
        .map((each) => Number(each))
        .filter((at) => Number.isInteger(at) && at >= 0);
}

/** The replies kept on a block, or nothing where it holds none. */
function repliesOf(value: string): readonly string[] | undefined {
  if (value === "") {
    return undefined;
  }

  try {
    const replies: unknown = JSON.parse(value);

    return Array.isArray(replies) &&
      replies.every((each) => typeof each === "string")
      ? replies
      : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The kept place and the keeper for a block that names where to post, or
 * nothing for a block that keeps no place.
 * @param block - The block.
 * @param fetching - How to post.
 * @returns What was kept, or nothing.
 */
export function keptReading(
  block: HTMLElement,
  fetching: typeof fetch,
): { readonly kept: YueduPlace; readonly keeper: YueduKeeper } | undefined {
  const address = said(block, recordAttribute);
  const slug = said(block, exerciseAttribute);

  if (address === "" || slug === "") {
    return undefined;
  }

  return {
    kept: {
      queue: indexes(said(block, queueAttribute)),
      missed: indexes(said(block, missedAttribute)),
      choices: choicesFrom(
        said(block, choicesAttribute)
          .split(",")
          .filter((each) => each !== "")
          .map(Number),
        Number(block.dataset["cbItems"] ?? ""),
      ),
      replies: repliesOf(said(block, repliesAttribute)),
      tries: said(block, triesAttribute)
        .split(",")
        .filter((each) => each !== "")
        .map(Number),
      unchecked: indexes(said(block, uncheckedAttribute)),
    },
    keeper: {
      press: new ExercisePress({ fetch: fetching, address, slug }),
      version: block.dataset[versionAttribute] ?? "",
      handedOver: block.dataset[handedOverAttribute] !== undefined,
      handoverCount: blockHandoverCount(block),
      free: block.dataset[freeAttribute] !== undefined,
    },
  };
}

/** The review's parts, or nothing for a block drawn without them. */
function reviewParts(block: HTMLElement): YueduReviewParts | undefined {
  const nav = block.querySelector<HTMLElement>("[data-cb-yuedu-review-nav]");
  const button = (name: string): HTMLButtonElement | null =>
    block.querySelector<HTMLButtonElement>(`[data-cb-yuedu-${name}]`);
  const previous = button("review-previous");
  const next = button("review-next");
  const back = button("review-back");

  if (nav === null || previous === null || next === null || back === null) {
    return undefined;
  }

  return {
    nav,
    marks: [
      ...nav.querySelectorAll<HTMLButtonElement>("[data-cb-yuedu-review-at]"),
    ],
    previous,
    next,
    back,
    open: button("review"),
  };
}

/** Bind the review's buttons to the Exercise. */
function bindReview(exercise: YueduExercise, review: YueduReviewParts): void {
  review.open?.addEventListener("click", () => {
    exercise.review(0);
    review.marks[0]?.focus();
  });
  review.marks.forEach((button, at) => {
    button.addEventListener("click", () => {
      exercise.review(at);
    });
  });
  review.previous.addEventListener("click", () => {
    exercise.reviewBy(-1);
  });
  review.next.addEventListener("click", () => {
    exercise.reviewBy(1);
  });
  review.back.addEventListener("click", () => {
    exercise.leaveReview();
  });
}

/** Build the Exercise inside one block. */
function openReading(
  block: HTMLElement,
  fetching: typeof fetch,
  hear: HearVerdict | undefined,
): void {
  const questions = [
    ...block.querySelectorAll<HTMLFormElement>("form[data-cb-yuedu-question]"),
  ];
  const lines = [
    ...block.querySelectorAll<HTMLElement>("[data-cb-yuedu-line]"),
  ];
  const progress = block.querySelector<HTMLElement>("[data-cb-yuedu-progress]");
  const meter = block.querySelector<HTMLElement>("[data-cb-yuedu-meter]");
  const finished = block.querySelector<HTMLElement>("[data-cb-yuedu-finished]");
  const score = block.querySelector<HTMLElement>("[data-cb-yuedu-score]");
  const share = block.querySelector<HTMLElement>("[data-cb-yuedu-share]");
  const finishedLine = block.querySelector<HTMLElement>(
    "[data-cb-yuedu-finished-line]",
  );

  if (
    questions.length === 0 ||
    progress === null ||
    finished === null ||
    score === null ||
    finishedLine === null
  ) {
    return;
  }

  const review = reviewParts(block);

  const kept = keptReading(block, fetching);
  const exercise = new YueduExercise({
    questions,
    lines,
    body: block,
    progress,
    meter,
    finished,
    score,
    share,
    finishedLine,
    review,
    kept: kept?.kept,
    keeper: kept?.keeper,
    gate: itemGate(block),
    hear,
  });

  questions.forEach((form, at) => {
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      exercise.check(at);
    });
    form
      .querySelector("[data-cb-yuedu-next]")
      ?.addEventListener("click", () => {
        exercise.next();
      });
    optionsOf(form).forEach((radio, option) => {
      radio.closest(optionRow)?.addEventListener("click", (event) => {
        if (pressedRow(event)) {
          exercise.choose(at, option);
        }
      });
    });
  });

  // The keys are heard on the whole page, so a block taken off it stops
  // hearing them at the next key, rather than answering for a page gone.
  const onKey = (event: KeyboardEvent): void => {
    if (!block.isConnected) {
      block.ownerDocument.removeEventListener("keydown", onKey);

      return;
    }

    if (exercise.reviewing) {
      reviewByKey(exercise, event);

      return;
    }

    const at = questions.findIndex((form) => form.hidden === false);
    const form = questions[at];

    if (form !== undefined) {
      pickByKey(exercise, form, at, event);
    }
  };

  block.ownerDocument.addEventListener("keydown", onKey);

  for (const button of block.querySelectorAll<HTMLButtonElement>(
    "[data-cb-yuedu-english]",
  )) {
    button.addEventListener("click", () => {
      toggleEnglish(block, button);
    });
  }

  block
    .querySelector("[data-cb-yuedu-again]")
    ?.addEventListener("click", () => {
      exercise.again();
    });

  if (review !== undefined) {
    bindReview(exercise, review);
  }

  exercise.start();
  block.dataset["cbLianxiReady"] = "";
}

/**
 * In the review, the left and right arrows move a question back and on, and
 * Escape goes back to the finish. A key a field or an open popover has taken
 * is left to it.
 */
function reviewByKey(exercise: YueduExercise, event: KeyboardEvent): void {
  if (event.defaultPrevented || typingInto(event.target) || modified(event)) {
    return;
  }

  const step =
    event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;

  if (step !== 0) {
    event.preventDefault();
    exercise.reviewBy(step);
  } else if (event.key === "Escape") {
    exercise.leaveReview();
  }
}

/**
 * An option's letter or its number answers with it, and Enter answers with
 * the option an arrow moved to, until the question is marked. The keys are
 * heard anywhere on the page, so a reader need not click into the question
 * first, but never while typing. Enter on a button or a link is that
 * control's own, so Enter on an English button shows the English rather than
 * answering. A key held with a modifier is the
 * browser's.
 */
function pickByKey(
  exercise: YueduExercise,
  form: HTMLFormElement,
  at: number,
  event: KeyboardEvent,
): void {
  const options = optionsOf(form);
  const option = optionByKey(event.key, options.length);

  if (
    options.length === 0 ||
    options.some((each) => each.disabled) ||
    typingInto(event.target) ||
    modified(event)
  ) {
    return;
  }

  if (event.key === "Enter") {
    if (ownEnter(event.target)) {
      return;
    }

    event.preventDefault();
    form.requestSubmit();

    return;
  }

  if (option === undefined) {
    return;
  }

  const chosen = exercise.choose(at, option);

  if (chosen !== undefined) {
    event.preventDefault();

    if (!chosen.disabled) {
      chosen.focus();
    }
  }
}

/** Show the English a button names, or hide it again. */
function toggleEnglish(block: HTMLElement, button: HTMLButtonElement): void {
  const english = block.ownerDocument.getElementById(
    button.getAttribute("aria-controls") ?? "",
  );

  if (english === null) {
    return;
  }

  const showing = english.hidden === false;

  english.hidden = showing;
  button.setAttribute("aria-expanded", String(!showing));
}

/** What calls back once an element comes near the reader, once. */
export type WhenNear = (element: HTMLElement, then: () => void) => void;

/**
 * Call back once the element is within a screen of the viewport, or takes the
 * focus. A browser with no `IntersectionObserver` calls back at once.
 */
export const whenNear: WhenNear = (element, then) => {
  const view = element.ownerDocument.defaultView;

  if (view === null || !("IntersectionObserver" in view)) {
    then();

    return;
  }

  let called = false;
  const once = (): void => {
    if (!called) {
      called = true;
      observer.disconnect();
      element.removeEventListener("focusin", once);
      then();
    }
  };
  const observer = new view.IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        once();
      }
    },
    { rootMargin: "0px 0px 100% 0px" },
  );

  observer.observe(element);
  element.addEventListener("focusin", once);
};

/**
 * Start the reading Exercise on every block under `root`.
 * @param root - The page, or the part of it to look in.
 * @param fetching - How to post. The window's own unless a test says otherwise.
 * @param soundsFor - What plays a verdict's note. 复习's notes unless a test
 *   says otherwise.
 * @param near - When the questions are near enough to ask for the place.
 */
export function startYuedu(
  root: ParentNode,
  fetching: typeof fetch = (input, init): Promise<Response> =>
    fetch(input, init),
  soundsFor: (page: Document) => HearVerdict | undefined = verdictSounds,
  near: WhenNear = whenNear,
): void {
  for (const block of root.querySelectorAll<HTMLElement>(exerciseSelector)) {
    const hear = soundsFor(block.ownerDocument);

    if (wantsPlace(block)) {
      const questions =
        block.querySelector<HTMLElement>("[data-cb-yuedu-questions]") ?? block;

      near(questions, () => {
        void loadPlace(block, fetching).then(() => {
          openReading(block, fetching, hear);
        });
      });
    } else {
      openReading(block, fetching, hear);
    }
  }
}
